import { platformMyDayHostCapability, platformOptionalFactProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectRecordPolicyCapability } from '@crest/module-projects/contracts/record-policy';
import { projectsProjectReferenceCapability } from '@crest/module-projects/contracts/project-reference';
import { schedulingAssistantContextCapability, schedulingAssistantContextProviderId, } from './contracts/scheduling-assistant-context.contract.js';
import { schedulingEventLocationSynchronizedEventName, schedulingProjectUpdatedConsumerEventName, schedulingProjectUpdatedSubscriberContract, } from './contracts/scheduling-project-updated-consumer.contract.js';
export const schedulingModuleManifest = defineCrestModule({
    id: 'scheduling',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.request-authority-hold.v1',
        'platform.membership-directory.v1',
        'platform.migration-run-ledger.v1',
        'platform.import-resolution.v1',
        'projects.projects.v1',
        projectsProjectRecordPolicyCapability,
        projectsProjectReferenceCapability,
        'projects.import-resolution.v1',
        'projects.project-reference-access-hold.v1',
        platformMyDayHostCapability,
        platformOptionalFactProviderHostCapability,
    ],
    optionalCapabilities: [],
    optionalCapabilityFallbacks: [],
    provides: [
        'scheduling.events.v1',
        'scheduling.events.v2',
        'scheduling.event-owner-commands.v1',
        'scheduling.event-record-history.v1',
        'scheduling.event-record-policy.v1',
        'scheduling.event-revision-root.v1',
        schedulingAssistantContextCapability,
        'scheduling.events-import.v1',
    ],
    permissions: [
        'scheduling.events.read',
        'scheduling.events.create',
        'scheduling.events.update',
        'scheduling.events.delete',
        'scheduling.events.manage-all',
        'scheduling.events.read-history',
        'scheduling.events.restore',
        'scheduling.import.execute',
        'scheduling.import.rollback',
    ],
    settings: ['scheduling.copy.events', 'scheduling.copy.calendar'],
    copyCatalogs: ['scheduling.copy.events', 'scheduling.copy.calendar'],
    events: {
        publishes: [
            'scheduling.event-created.v1',
            'scheduling.event-updated.v1',
            'scheduling.event-deleted.v1',
            schedulingEventLocationSynchronizedEventName,
        ],
        subscribes: [schedulingProjectUpdatedConsumerEventName],
    },
    server: {
        routes: [{ id: 'scheduling.routes.events', priority: 0 }],
        jobs: [{ id: 'scheduling.jobs.project-address-sync-replay', priority: 10 }],
        subscribers: [{
                id: schedulingProjectUpdatedSubscriberContract,
                requiredCapability: 'projects.projects.v1',
                priority: 0,
            }],
        slots: [{
                id: schedulingAssistantContextProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'scheduling.events.read',
                priority: 10,
            }],
    },
    web: {
        routes: [{ id: 'scheduling.web.events', priority: 0 }],
        navigation: [{ id: 'scheduling.navigation.events', priority: 40 }],
        slots: [{
                id: 'scheduling.web.platform.my-day',
                priority: 15,
                requiredCapability: platformMyDayHostCapability,
                requiredPermission: 'scheduling.events.read',
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: { routes: [], slots: [] },
    database: {
        schema: 'scheduling',
        owns: [
            'scheduling.events',
            'scheduling.event_assignments',
            'scheduling.project_update_consumer_receipts',
            'scheduling.event_location_sync_facts',
            'scheduling.paused_project_update_deliveries',
            'scheduling.event_versions',
            'scheduling.event_commands',
            'scheduling.event_command_results',
            'scheduling.events_import_runs',
            'scheduling.events_import_identity_map',
            'scheduling.events_import_receipts',
            'scheduling.events_import_changes',
            'scheduling.events_import_rejects',
            'scheduling.events_import_rollbacks',
            'scheduling.before_owned_record_update',
            'scheduling.reject_project_update_evidence_mutation',
            'scheduling.consume_project_updated_v2',
            'scheduling.reject_paused_delivery_mutation',
            'scheduling.project_address_sync_replay_v1',
            'scheduling.ecmascript_trim_v1',
            'scheduling.utf16_length_v1',
            'scheduling.canonical_timestamp_v1',
            'scheduling.canonical_event_snapshot_v1',
            'scheduling.sha256_event_snapshot_v1',
            'scheduling.sha256_event_command_input_v1',
            'scheduling.current_membership_can_read_event_v1',
            'scheduling.current_membership_can_read_event_history_v1',
            'scheduling.lock_event_revision_root_v1',
            'scheduling.write_event_revision_v1',
            'scheduling.execute_event_owner_command_v1',
            'scheduling.assert_current_event_version_v1',
            'scheduling.enforce_event_owner_context_v1',
            'scheduling.require_current_event_version_v1',
            'scheduling.reject_event_physical_delete_v1',
            'scheduling.reject_event_evidence_mutation_v1',
            'scheduling.require_event_command_result_v1',
            'scheduling.events_import_evidence_immutable_v1',
            'scheduling.events_import_canonical_json_v1',
            'scheduling.events_import_sha256_v1',
            'scheduling.events_import_timestamp_v1',
            'scheduling.events_import_exact_keys_v1',
            'scheduling.events_import_assignment_id_v1',
            'scheduling.events_import_assignment_canonical_v1',
            'scheduling.events_import_event_canonical_v1',
            'scheduling.events_import_receipt_json_v1',
            'scheduling.events_import_assert_membership_resolution_v1',
            'scheduling.events_import_assert_project_resolution_v1',
            'scheduling.begin_events_import_v1',
            'scheduling.quarantine_events_import_v1',
            'scheduling.apply_events_import_v1',
            'scheduling.verify_events_import_receipt_v1',
            'scheduling.rollback_events_import_v1',
        ],
        readsThrough: [
            'platform.request-authority-hold.v1',
            'platform.membership-directory.v1',
            'platform.migration-run-ledger.v1',
            'platform.import-resolution.v1',
            projectsProjectRecordPolicyCapability,
            projectsProjectReferenceCapability,
            'projects.import-resolution.v1',
            'projects.project-reference-access-hold.v1',
        ],
        migrations: [
            'scheduling.events_foundation',
            'scheduling.event_lifecycle_project_address_sync',
            'scheduling.project_address_sync_pause_replay',
            'scheduling.event_owner_foundation',
            'scheduling.events_import',
        ],
    },
    health: ['scheduling.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 1,
            queueBacklogLimit: 500,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'pause-and-replay',
                durableCursor: true,
                retentionDays: 30,
                backlogLimit: 100_000,
                warningAt: 50_000,
                reconciliationProcedure: 'scheduling.project-address-sync-replay.v1',
                dropSafeReason: null,
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['scheduling.database'],
            reconciliationProcedure: 'scheduling.project-address-sync-replay.v1',
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Scheduling data remains stored while the optional module is disabled.',
});
//# sourceMappingURL=scheduling.module.js.map